import { Injectable, Inject } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Fortigate } from '@app/shared/models/fortigate/fortigate.model';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { timeout, retry, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class FortigatesService {
    private apiUrl = `${environment.apiHost}/fortigate`;

    constructor(private http: HttpClient, @Inject(OKTA_AUTH) public oktaAuth: OktaAuth) {
    }

    async findByStoreNumber(storeNumber: string) {
        return this.http.get<Fortigate>(`${this.apiUrl}/routers?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(2000),
                retry(3),
                catchError(error => of("Requests for fortigates timed out!"))
            );
    }
    
    async findByStoreNumberV2(storeNumber: string) {
        return this.http.get<Fortigate>(`${this.apiUrl}/routers?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(5000),
                retry(3),
                catchError(error => of("Requests for fortigates timed out!"))
            );
    }

    async getAllFortigates() {
        return this.http.get(`${this.apiUrl}/all-routers-latent`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Requests for all fortigates timed out!"))
            );
    }

    async getRouterGlobalStatistics(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/global-statistics?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Requests for router global statistics timed out!"))
            );
    }

    async getPrimaryVpnStatus(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/primary-vpn-status?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Requests for primary vpn status timed out!"))
            );
    }

    async getVpnStatus(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/vpn-status?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => error.status === 404 ? of(null) : of("Requests for primary vpn status timed out!"))
            );
    }
    
    async getActiveVpnStatuses(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/active-vpn-statuses?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => error.status === 404 ? of(null) : of("Requests for active VPN statuses timed out!"))
            );
    }

    // Live wan1/wan2 state read off the device itself. Returns 200 with an
    // "unavailable" array listing hostnames that could not be reached, so an empty
    // result can be told apart from a failed lookup.
    async getWanInterfaces(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/wan-interfaces?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Requests for WAN interfaces timed out!"))
            );
    }

    async getWan1Interface(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/config/interface/wan-1?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Requests for wan 1 interface timed out!"))
            );
    }

    async getGatewayInfo(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/gateway-info?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Requests for gateway info timed out!"))
            );
    }

    async getSmartSafeStatus(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/smartsafe-status?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(3),
                catchError(error => of("Requests for SmartSafe status timed out!"))
            );     
    }

    async getAvailableAdoms() {
        return this.http.get(`${this.apiUrl}/provision/get-available-adoms`,   
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                ),
                observe: 'response'
            }).pipe(
                timeout(10000),
                catchError(error => of(error))
            );  
    }
    
    async getLanFields(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/provision/get-computed-lan-fields?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                ),
                observe: 'response'
            }).pipe(
                timeout(10000),
                catchError(error => of(error))
            );  
    }

    async getWanFields(storeNumber: string) {
        return this.http.get(`${this.apiUrl}/provision/get-computed-wan-fields?storeNumber=${storeNumber}`,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                ),
                observe: 'response'
            }).pipe(
                timeout(10000),
                catchError(error => of(error))
            );
    }

    async provisionFull(payload: any) {
        return this.http.post(`${this.apiUrl}/provision/full`,
            payload,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                ),
                observe: 'response'
            }).pipe(
                // provision/full runs create + quick-install + FortiZTP + clean-up
                // server-side; Lambda timeout is 300s, so allow the same here.
                timeout(300000),
                catchError(error => of(error))
            );
    }
}